import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContextProvider';
import { loadTenantConfig } from './TenantLoader';
import { Box, Button, Typography, Alert, Paper } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import MicrosoftIcon from '@mui/icons-material/Microsoft';

export default function Login() {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tenantConfig, setTenantConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const { loginWithGoogle, loginWithMicrosoft } = useAuth();

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = await loadTenantConfig();
        setTenantConfig(config);
      } catch (error) {
        console.error('Failed to load tenant config:', error);
      } finally {
        setConfigLoading(false);
      }
    };

    loadConfig();
  }, []);

  async function handleGoogleLogin() {
    try {
      setError('');
      setLoading(true);
      await loginWithGoogle();
    } catch (error) {
      setError('Failed to sign in with Google: ' + error.message);
    }
    setLoading(false);
  }

  async function handleMicrosoftLogin() {
    try {
      setError('');
      setLoading(true);
      await loginWithMicrosoft();
    } catch (error) {
      setError('Failed to sign in with Microsoft: ' + error.message);
    }
    setLoading(false);
  }

  if (configLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: 'background.default'
      }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!tenantConfig) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: 'background.default'
      }}>
        <Typography color="error">Failed to load configuration</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      backgroundColor: 'background.default'
    }}>
      <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%' }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          {tenantConfig.login.title}
        </Typography>
        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
          {tenantConfig.login.subtitle}
        </Typography>
        
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            size="large"
            startIcon={<GoogleIcon />}
            onClick={handleGoogleLogin}
            disabled={loading}
            sx={{ 
              borderColor: '#4285f4', 
              color: '#4285f4',
              '&:hover': {
                borderColor: '#3367d6',
                backgroundColor: 'rgba(66, 133, 244, 0.04)'
              }
            }}
          >
            Sign in with Google
          </Button>
          
          <Button
            fullWidth
            variant="outlined"
            size="large"
            startIcon={<MicrosoftIcon />}
            onClick={handleMicrosoftLogin}
            disabled={loading}
            sx={{ 
              borderColor: '#00a1f1', 
              color: '#00a1f1',
              '&:hover': {
                borderColor: '#0078d4',
                backgroundColor: 'rgba(0, 161, 241, 0.04)'
              }
            }}
          >
            Sign in with Microsoft
          </Button>
        </Box>
      </Paper>
    </Box>
  );
} 